import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface Endpoint {
  operation: string;
  method: string;
  path: string;
  contentType: string;
  requestTemplate: string | null;
  successCondition: string | null;
  responseMapping: { extract?: Record<string, string> };
  isActive: boolean;
}

interface Preview {
  method: string;
  url: string;
  headers: Record<string, unknown>;
  body: unknown;
  errors: Array<{ field: string; message: string; position?: number }>;
  credential: { configured: boolean; hint: string };
  contextFields: string[];
}

interface OrderOption {
  id: string;
  orderNo: string;
  receiverName: string;
}

/**
 * 字段映射编辑器。
 *
 * ★ 这一屏是整个「不改代码就能接新平台」方案能不能落地的分水岭。
 *
 *   没有实时预览的话,实施人员写完模板只能**直接推一单真的出去试** ——
 *   而那一单已经在对方系统里建好了,撤不回来,还得打电话请客服删。
 *   所以左边写表达式、右边立刻看到「将要发出去的东西」,
 *   而且是拿一张真实订单渲染的,不是假数据。
 */
export function EndpointEditor({
  channelId,
  channelName,
  operation,
  existing,
  onClose,
  onSaved,
}: {
  channelId: string;
  channelName: string;
  operation: string;
  existing?: Endpoint;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [method, setMethod] = useState(existing?.method ?? 'POST');
  const [path, setPath] = useState(existing?.path ?? '/');
  const [template, setTemplate] = useState(
    existing?.requestTemplate ?? (operation === 'NOTIFY_STATUS' ? NOTIFY_SAMPLE : SAMPLE),
  );
  const [successCondition, setSuccessCondition] = useState(existing?.successCondition ?? '');
  const [extract, setExtract] = useState(
    JSON.stringify(existing?.responseMapping?.extract ?? { channelOrderNo: 'body.data.waybillNo' }, null, 2),
  );

  const [orders, setOrders] = useState<OrderOption[]>([]);
  const [orderId, setOrderId] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      // 拿最近几单当样本 —— 预览必须用真实订单,假数据看不出字段对不对
      const list = await api<{ data: OrderOption[] }>('/orders?limit=10');
      setOrders(list.data);
      setOrderId((cur) => cur || (list.data[0]?.id ?? ''));
    })().catch(() => setError('取不到样本订单'));
  }, []);

  // 边打字边渲染,但要防抖 —— 每敲一个字符打一次接口既慢又吵
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!orderId) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPreviewing(true);
      void api<Preview>(`/integrations/channels/${channelId}/endpoints/${operation}/preview`, {
        method: 'POST',
        body: { orderId, method, path, requestTemplate: template },
      })
        .then(setPreview)
        .catch((e) => setError(e instanceof ApiFailure ? e.message : '预览失败'))
        .finally(() => setPreviewing(false));
    }, 400);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [channelId, operation, orderId, template, method, path]);

  const templateError = useMemo(
    () => preview?.errors.find((e) => e.field === 'requestTemplate') ?? null,
    [preview],
  );

  async function save() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      let parsedExtract: Record<string, string>;
      try {
        parsedExtract = JSON.parse(extract) as Record<string, string>;
      } catch {
        throw new Error('「取哪些字段」不是合法的 JSON');
      }
      await api(`/integrations/channels/${channelId}/endpoints/${operation}`, {
        method: 'PUT',
        body: {
          method,
          path,
          requestTemplate: template,
          successCondition: successCondition.trim() || undefined,
          responseMapping: { extract: parsedExtract, errorMapping: {} },
          isActive: true,
        },
      });
      await onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="字段映射"
        className="w-full max-w-6xl max-h-[92dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            字段映射 · {channelName} · {operation}
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-500">
          左边写映射,右边是拿一张真实订单渲染出来的、将要发出去的请求。
          {operation === 'NOTIFY_STATUS' && (
            <span className="text-slate-400">
              {' '}
              状态用 <code className="font-mono">trigger.status</code>(排队那一刻的),
              不是 <code className="font-mono">order.status</code>。
            </span>
          )}
        </p>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          {/* ── 左:配置 ── */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <select
                aria-label="请求方法"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="h-10 px-2 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
              >
                {['POST', 'GET', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
              <input
                aria-label="路径"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder="/v1/orders"
                className="flex-1 h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm font-mono"
              />
            </div>

            <label className="block">
              <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                请求体映射(JSONata)
              </span>
              <textarea
                aria-label="请求体映射"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                spellCheck={false}
                rows={16}
                className={`w-full px-3 py-2 rounded-lg border font-mono text-xs leading-relaxed
                            bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
                            ${
                              templateError
                                ? 'border-red-400 dark:border-red-700'
                                : 'border-slate-300 dark:border-slate-600'
                            }`}
              />
            </label>

            {/* ★ 错误要指到第几个字符。只说「表达式有误」的话,
                   人只能一段段删着试 */}
            {templateError && (
              <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                              dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {templateError.message}
                {templateError.position !== undefined && (
                  <span className="text-red-500"> (第 {templateError.position} 个字符附近)</span>
                )}
              </div>
            )}

            <label className="block">
              <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                成功判定
                <span className="text-slate-400">
                  {' '}
                  —— 很多平台回 200 但 body 里是失败,不能只看状态码
                </span>
              </span>
              <input
                aria-label="成功判定"
                value={successCondition}
                onChange={(e) => setSuccessCondition(e.target.value)}
                spellCheck={false}
                placeholder="status = 200 and body.code = '0000'"
                className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-xs font-mono"
              />
            </label>

            <label className="block">
              <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                从响应里取哪些字段
              </span>
              <textarea
                aria-label="取哪些字段"
                value={extract}
                onChange={(e) => setExtract(e.target.value)}
                spellCheck={false}
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
                           font-mono text-xs"
              />
            </label>

            {/* 可用字段列出来 —— 否则只能靠猜字段名 */}
            {preview && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500">
                  可用字段({preview.contextFields.length})
                </summary>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {preview.contextFields.map((f) => (
                    <button
                      key={f}
                      onClick={() => setTemplate((t) => `${t}${f}`)}
                      className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700
                                 text-slate-600 dark:text-slate-300 font-mono"
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </details>
            )}
          </div>

          {/* ── 右:预览 ── */}
          <div className="space-y-3">
            <label className="block">
              <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                拿哪一单预览
              </span>
              <select
                aria-label="预览订单"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
              >
                {orders.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.orderNo} · {o.receiverName}
                  </option>
                ))}
              </select>
            </label>

            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
              <div className="px-3 py-2 bg-slate-50 dark:bg-slate-900/50 text-xs
                              text-slate-600 dark:text-slate-400 flex items-center justify-between">
                <span className="font-mono truncate">{`${method} ${preview?.url ?? '—'}`}</span>
                {previewing && <span className="text-slate-400 shrink-0 ml-2">渲染中…</span>}
              </div>
              <pre
                aria-label="预览结果"
                className="p-3 text-xs font-mono overflow-x-auto max-h-[420px]
                           text-slate-800 dark:text-slate-200 whitespace-pre"
              >
                {preview?.body !== undefined && preview.body !== null
                  ? JSON.stringify(preview.body, null, 2)
                  : templateError
                    ? '（表达式有误，见左侧）'
                    : '—'}
              </pre>
            </div>

            {/* 预览里的手机号是打码的 —— 说明一句,否则会被当成映射错了 */}
            <p className="text-xs text-slate-400">
              预览里的手机号和地址是打了码的(集成配置的查看权限比订单宽),
              真正发出去的是完整值。
            </p>

            {preview && !preview.credential.configured && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200
                              dark:border-amber-900 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
                {preview.credential.hint}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300"
          >
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={saving || Boolean(templateError)}
            className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
          >
            {saving ? '保存中…' : templateError ? '表达式有误,无法保存' : '保存映射'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 状态回推的默认模板。
 *
 * ★ 用 `trigger.status` 而不是 `order.status`:
 *   任务从排队到真发出去中间隔着轮询和重试,读当前状态的话,
 *   商家会连着收到两条「已送达」,而「已取件」那条永远不会到。
 */
const NOTIFY_SAMPLE = `{
  "orderNo": order.orderNo,
  "externalRef": order.externalRef,
  "status": trigger.status,
  "occurredAt": trigger.occurredAt,
  "receiver": { "name": order.receiverName }
}`;

const SAMPLE = `{
  "externalOrderNo": order.orderNo,
  "receiver": {
    "name": order.receiverName,
    "phone": $phoneE164(order.receiverPhone),
    "address": order.receiverAddress
  },
  "parcel": { "qty": order.packageCount },
  "cod": $money(order.codAmount)
}`;
